import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';
import { EmployeeOption } from '@/modules/shared/hr-lookups';
import { useScopedEmployeeSearch } from '../../hooks/use-leave-manager';
import { LeaveSheetView } from '../leave-sheet-view/leave-sheet-view';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Employee dropdown + their leave sheet (the default leave ledger of an HR term). The dropdown searches
 * on the server within the Leave Manager's scope (`useScopedEmployeeSearch`).
 */
export const LeaveSheetTab = () => {
  const { t } = useTranslation();
  const [employee, setEmployee] = useState<EmployeeOption | null>(null);
  const [input, setInput] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(input.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  // Only the employees the Leave Manager lists (admins: all; heads: their departments).
  const { data: employees = [], isLoading } = useScopedEmployeeSearch(keyword);
  const options = employees.map((option) => ({
    value: option.UserId,
    label: option.DisplayName,
    description: [option.Designation, option.DepartmentName, option.Email]
      .filter(Boolean)
      .join(' · '),
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-md">
        <SearchableSelect
          value={employee?.UserId ?? ''}
          options={options}
          onChange={(userId) =>
            setEmployee(employees.find((option) => option.UserId === userId) ?? null)
          }
          onSearchChange={setInput}
          selectedLabel={employee?.DisplayName}
          placeholder={t('SELECT_EMPLOYEE')}
          searchPlaceholder={t('SEARCH_EMPLOYEE')}
          emptyText={t('NO_DATA_FOUND')}
          isLoading={isLoading}
        />
      </div>

      {employee && <LeaveSheetView key={employee.UserId} userId={employee.UserId} canPickTerm />}

      {!employee && (
        <p className="text-sm text-muted-foreground">{t('SELECT_EMPLOYEE_TO_VIEW_LEAVE_SHEET')}</p>
      )}
    </div>
  );
};
